import { useEffect, useCallback, useRef } from 'react';
import { ObjectId } from 'bson';
import moment from 'moment-timezone';
import { useGeneralContext } from '@context/GeneralContext';
import { useLambdaFunctionContext } from '@context/LambdaFunctionContext';
import { useCartContext } from '@context/CartContext';
import { useStoreOrderNumber } from '@state/useStoreOrderNumber';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { standardizeData } from '@libs/standardizeData';
import { useCheckoutState } from '@state/checkout/useCheckoutState';
import { useShallow } from 'zustand/react/shallow';
import { useStoreCartItems } from '@state/useStoreCartItems';
import { useTranslation } from 'react-i18next';
import { sendErrorData } from '@functions/ErrorMessagesSender';

function usePrepareBill() {
  const { t } = useTranslation();
  const { settings, getOrderMethod } = useStoreSettings((state) => ({
    settings: state.settings,
    getOrderMethod: state.getOrderMethod,
  }));

  const hasServiceCharge = settings.hasServiceCharge;

  const { getContextState, prepServiceCharge, setContextState } =
    useCartContext();
  const paymentResId = getContextState('paymentResId');

  const { showLoading, showServiceLoading, openErrorSnackBar } =
    useGeneralContext();
  const { getDetailTransaction } = useLambdaFunctionContext();
  const { updateOrderNumber } = useStoreOrderNumber((state) => ({
    updateOrderNumber: state.updateOrderNumber,
  }));

  const { cartItems } = useStoreCartItems((state) => ({
    cartItems: state.cartItems,
  }));

  const { outlet: outletInfo } = useStoreOutlet((state) => ({
    outlet: state.outlet,
  }));
  const {
    updateBillTaxes,
    updateBillFooterNotes,
    updateBillGrandTotal,
    updateBillSubTotal,
    updateAmountCurrency,
    updateTableName,
    updateServiceCharge,
    updateAmountServiceCharge,
    // updateBillDiscount
  } = useCheckoutState(
    useShallow((state) => ({
      updateBillTaxes: state.updateBillTaxes,
      updateBillFooterNotes: state.updateBillFooterNotes,
      updateBillGrandTotal: state.updateBillGrandTotal,
      updateBillSubTotal: state.updateBillSubTotal,
      updateAmountCurrency: state.updateAmountCurrency,
      updateTableName: state.updateTableName,
      updateServiceCharge: state.updateServiceCharge,
      updateAmountServiceCharge: state.updateAmountServiceCharge,
      // updateBillDiscount: state.updateBillDiscount
    })),
  );

  // Track if prepItemService has already run
  const hasRunPrepItemService = useRef(false);

  // Keep a ref to always have the latest cartItems in comparingSubtotal                                                                                                             
  const cartItemsRef = useRef(cartItems);                                                                                                                                            
  cartItemsRef.current = cartItems;        

  const comparingSubtotal = (fetchSubTotal) => {
    const transaction_items = [...fetchSubTotal.transaction_items];
    const transactionMap = new Map(
      transaction_items.map((item) => [item.plu_number, item.quantity]),
    );
    const isValidItem = cartItemsRef.current.some((cartItem) => {   
      if (cartItem.is_variant) {
        return cartItem.variant_item.some((varItem) => {
          const transactionQty = transactionMap.get(varItem.plu_number);
          // Validasi tipe data untuk menghindari undefined === undefined
          if (
            typeof transactionQty !== 'number' ||
            typeof varItem.quantity !== 'number'
          ) {
            return false;
          }
          return transactionQty === varItem.quantity;
        });
      } else {
        const transactionQty = transactionMap.get(cartItem.plu_number);
        // Validasi tipe data untuk menghindari undefined === undefined
        if (
          typeof transactionQty !== 'number' ||
          typeof cartItem.quantity !== 'number'
        ) {
          return false;
        }
        return transactionQty === cartItem.quantity;
      }
    });

    // Jika item tidak valid tampilkan modal warning service charge
    if (!isValidItem) {
      setContextState({
        subtotalWarning: {
          visible: true,
        },
      });
    }
  };

  const prepItemService = useCallback(async () => {
    // Only run once
    if (hasRunPrepItemService.current) {
      return;
    }
    hasRunPrepItemService.current = true;

    if (hasServiceCharge) {
      showServiceLoading(true);
    }

    const serviceCharge = await prepServiceCharge();
    if (serviceCharge && !serviceCharge.error) {
      if (hasServiceCharge) {
        showServiceLoading(false);
      }
      updateServiceCharge(serviceCharge);
      updateAmountServiceCharge(serviceCharge.amount);
    }
    if (hasServiceCharge) {
      showServiceLoading(false);
    }
  }, [hasServiceCharge, showServiceLoading, prepServiceCharge, updateServiceCharge, updateAmountServiceCharge]);

  const prepareBillData = useCallback(
    async (transId, miniappReqId) => {
      let orderMethod = getOrderMethod();
      let filter = {
        _id: new ObjectId(transId),
        table_id: outletInfo.tableId,
        status: 'calculated',
        type_bill: orderMethod,
        createdAt: { $gt: moment().subtract(3, 'h').toDate() }
      };

      let queryTrans = null;
      try {
        if (miniappReqId && miniappReqId !== 'undefined') {
          queryTrans = await getDetailTransaction(filter);
        }

        if (
          queryTrans &&
          typeof queryTrans === 'object' &&
          Object.keys(queryTrans).length > 0 &&
          !queryTrans.is_split &&
          !(queryTrans.error)
        ) {
          let standardData = standardizeData(queryTrans);
          if (settings.customTable === 'raptor' || settings.customTable === 'rdo') {
            updateOrderNumber(queryTrans.table_name);
          }
          updateBillTaxes([...standardData.taxes]);
          // getBillDetail (fe-lambda 0.5.23+) passes the bill's footnotes, e.g. "* Not subject to GST"
          // when the PLU Surcharge is among the taxes
          updateBillFooterNotes(Array.isArray(queryTrans.footer_notes) ? [...queryTrans.footer_notes] : []);
          updateBillSubTotal(standardData.sub_total);
          updateBillGrandTotal(standardData.grand_total);
          updateAmountCurrency(outletInfo.currency);
          updateTableName(queryTrans.table_name);
          comparingSubtotal(queryTrans);
          // updateBillDiscount([...standardData.discounts])
          if (standardData.grand_total === 0) {
            setContextState({ paymentResId: { visible: false } });
            openErrorSnackBar(t('failedTax'));
            sendErrorData({
              message: 'tax gagal karna grand total 0',
              file: 'usePrepareBill.js',
              func: 'prepareBillData()',
            });
          }
        } else if (
          queryTrans &&
          typeof queryTrans === 'object' &&
          queryTrans.error
        ) {
          setContextState({ paymentResId: { visible: false } });
          openErrorSnackBar('No view bill found');
          sendErrorData({
            message: 'No view bill found',
            file: 'usePrepareBill.js',
            func: 'prepareBillData()',
          });
        } else {
          setContextState({ paymentResId: { visible: false } });
          openErrorSnackBar(t('failedTax'));
          sendErrorData({
            message: `tax gagal karna response ${queryTrans}`,
            file: 'usePrepareBill.js',
            func: 'prepareBillData()',
          });
        }
      } catch (e) {
        setContextState({ paymentResId: { visible: false } });
        openErrorSnackBar(t('failedTax'));
        sendErrorData({
          message: `tax gagal karna ${e}, response ${queryTrans}`,
          file: 'usePrepareBill.js',
          func: 'prepareBillData()',
        });
      }
      showLoading(false);
    },
    [paymentResId.trans_id, paymentResId.miniapp_request_id],
  );

  // Run prepItemService only once on mount
  useEffect(() => {
    if (settings.hasServiceCharge) {
      prepItemService();
    }
    // eslint-disable-next-line
  }, []);

  // Return prepareBillData to be called manually from CheckoutPage
  return { prepareBillData };
}

export default usePrepareBill;
